import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { inject } from '@app/providers/QAU.ContextProvider';
import LabeledToggle from '@components/labeled-toggle/LabeledToggle';
import LocalizedText from '@LocalizedText';
import PropTypes from 'prop-types';
import { setBgParams } from '@app/actions/customizeActions';
import {
  findRmbgImg,
  isCustomShape,
} from '@app/utils/utils';
import { getProjectContourCutOption } from '@app/utils/hints';
import './BackgroundTool.scss';

const toggleImgField = ({ visible, notVisible }, syncPosition, editor) => {
  const { transformTool, projectEditor } = editor;

  if (syncPosition) {
    const { width, height, rotation, position: { x, y } } = notVisible;
    projectEditor.rotateField(visible, rotation - visible.rotation);
    projectEditor.setPosition(visible, x, y);
    projectEditor.resizeField(visible, width, height);
  }

  visible.visible = true;
  visible.editable = true;
  visible.previewable = true;
  visible.print = true;
  visible.movable = true;
  notVisible.visible = false;
  notVisible.editable = false;
  notVisible.previewable = false;
  notVisible.print = false;
  notVisible.movable = false;
  transformTool.lockFields(notVisible);
  transformTool.unlockFields(visible);
  transformTool.selectFields([visible]);
  editor.bringToFront([visible], false);
};

const BackgroundTool = ({
  flow,
  context,
  bgParams: {
    artworkBgVisible,
    removeBgInProgress,
    bgVisibilityChanged,
    contourCutInProgress,
    contourCutChanged,
    contourCut,
  },
  createdFromDielineShape,
  isCustomShape,
  setBgParams,
  disabled,
}) => {
  const [rmbg, setRmbg] = useState(!artworkBgVisible);
  const [cCut, setCCut] = useState(contourCut);
  const onRmbgChange = () => {
    setRmbg(!rmbg);
    setBgParams({
      bgVisibilityChanged: artworkBgVisible !== rmbg,
    });
  };

  const onContourCutChange = () => setCCut(prev => !prev);

  useEffect(() => {
    setBgParams({
      contourCutChanged: contourCut !== cCut,
    });
  }, [contourCut, cCut]);

  useEffect(() => {
    setRmbg(!artworkBgVisible);
  }, [artworkBgVisible]);

  useEffect(() => {
    const { keep, removed } = findRmbgImg(flow);
    if (!!keep && !!removed && removed.visible) {
      setBgParams({ artworkBgVisible: false });
      setRmbg(true);
    }
  }, []);

  useEffect(() => {
    const projectContourCutOption = JSON.parse(getProjectContourCutOption(context));
    const { removed } = findRmbgImg(flow);
    setBgParams({ contourCut: projectContourCutOption && !!removed });
    setCCut(projectContourCutOption && !!removed);
  }, []);

  useEffect(() => {
    if (removeBgInProgress || contourCutInProgress) return;
    const { keep: keepImg, removed: removedImg } = findRmbgImg(flow);
    keepImg && removedImg && toggleImgField({
      visible: artworkBgVisible ? keepImg : removedImg,
      notVisible: artworkBgVisible ? removedImg : keepImg,
    }, bgVisibilityChanged, flow.editor);
    flow.editor.updateContent();
    setBgParams({
      ...(contourCutChanged && { contourCutChanged: !contourCutChanged }),
      ...(bgVisibilityChanged && { bgVisibilityChanged: !bgVisibilityChanged }),
    });
  }, [artworkBgVisible, removeBgInProgress, contourCutInProgress]);

  useEffect(() => {
    !bgVisibilityChanged && rmbg === artworkBgVisible && setRmbg(!artworkBgVisible);
    !contourCutChanged && cCut !== contourCut && setCCut(contourCut);
  }, [contourCutChanged, bgVisibilityChanged]);

  return <div className="background-tool-group">
    {isCustomShape && <div className="contour-cut-tool" aria-disabled={disabled || createdFromDielineShape}>
      <label htmlFor="contour-cut-tool-checkbox">
        <input type="checkbox" id="contour-cut-tool-checkbox" onChange={onContourCutChange} checked={cCut} />
        <LocalizedText id="qau-artwork-background-contour-cut" tagName="span" />
      </label>
    </div>}
    <div className="background-tool-wrapper">
      <div className="tool-group-label" aria-disabled={createdFromDielineShape}>
        <LocalizedText id="qau-artwork-background-title" tagName="b"/>
      </div>
      <div id="qau-artwork-background-tool" aria-disabled={createdFromDielineShape}>
        <LabeledToggle
          options={['keep', 'remove']}
          onChange={onRmbgChange}
          checked={rmbg ? 'remove' : 'keep'}
        />
      </div>
    </div>
  </div>;
};

const mapStateToProps = ({ customize: { customizeData: {
  bgParams,
  resizeParams: { selectedShape }
} } }) => ({
  bgParams,
  isCustomShape: isCustomShape(selectedShape),
});
const mapDispatchToProps = { setBgParams };

BackgroundTool.propTypes = {
  flow: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  bgParams: PropTypes.object.isRequired,
  setBgParams: PropTypes.func.isRequired,
  isCustomShape: PropTypes.bool.isRequired,
};

export default inject('flow', 'context')(connect(mapStateToProps, mapDispatchToProps)(BackgroundTool));
